import { applySeo } from "@/lib/seo-head";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Layout } from "@/components/site/Layout";
import { OfficeMap } from "@/components/site/OfficeMap";
import { Phone, Mail, MapPin } from "lucide-react";
import { listAllProductsPublic } from "@/lib/content.functions";
import { DefaultContactForm } from "@/components/forms/DefaultContactForm";
import { getFormByKey } from "@/lib/forms/registry";
import { useT } from "@/lib/i18n";

const productsQueryOptions = queryOptions({
  queryKey: ["all-products"],
  queryFn: () => listAllProductsPublic(),
});

export const Route = createFileRoute("/products/$slug")({
  component: ProductPage,
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(productsQueryOptions);
    const product = (data.products ?? []).find((p) => p.slug === params.slug && p.active);
    if (!product) throw notFound();
    return {
      productName: product.name,
      productDescription: product.description,
      priceCents: product.price_cents,
      currency: product.currency,
      priceless: Boolean(product.priceless),
    };
  },
  head: (ctx) => applySeo(ctx.matches, (({ loaderData, params }) => {
    // Loader threw notFound() (or errored): emit noindex and no canonical/og/JSON-LD
    // so Google drops URLs like /products/$slug or /products/<slug-inexistente>.
    if (!loaderData) {
      return {
        meta: [
          { title: "Producto no encontrado — RR Lex Juristas" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }
    const name = loaderData.productName;
    const desc = loaderData.productDescription;
    const url = `https://rrlexjuristas.com/products/${params.slug}`;
    const productJsonLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Product",
      name,
      description: desc,
      url,
      brand: { "@type": "Organization", name: "RR Lex Juristas" },
    };
    if (!loaderData.priceless && loaderData.priceCents > 0) {
      productJsonLd.offers = {
        "@type": "Offer",
        price: (loaderData.priceCents / 100).toFixed(2),
        priceCurrency: (loaderData.currency || "EUR").toUpperCase(),
        availability: "https://schema.org/InStock",
        url,
      };
    }
    return {
      meta: [
        { title: `${name} — RR Lex Juristas` },
        { name: "description", content: desc },
        { property: "og:title", content: `${name} — RR Lex Juristas` },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(productJsonLd),
        },
      ],
    };
  })(ctx)),
  errorComponent: ({ error }) => (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" role="alert">
        <h1 className="font-serif text-3xl mb-4">No se pudo cargar el producto</h1>
        <p className="text-muted-foreground">{(error as Error).message}</p>
      </div>
    </Layout>
  ),
  notFoundComponent: () => (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-3xl">Producto no encontrado</h1>
      </div>
    </Layout>
  ),
});

function ProductPage() {
  const { tk } = useT();
  const { slug } = Route.useParams();
  const { data: productsData } = useSuspenseQuery(productsQueryOptions);

  const product = (productsData.products ?? []).find((p) => p.slug === slug && p.active);
  if (!product) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="font-serif text-3xl">Producto no encontrado</h1>
        </div>
      </Layout>
    );
  }

  const formDef = getFormByKey(product.form_key);
  const FormComponent = formDef?.component;

  return (
    <Layout>
      {FormComponent ? <FormComponent product={product} /> : <DefaultContactForm />}

      <section className="border-t border-gold/15 bg-card/20">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8">
          {[
            { i: Phone, l: tk("contacto.26176f", "Teléfonos"), v: ["+34 665 649 354", "+34 954 13 66 37"] },
            { i: Mail, l: "Email", v: ["rrlexjuristas@icasevilla.org"] },
            { i: MapPin, l: tk("contacto.4e38ee", "Despachos"), v: ["Camas, Sevilla", "Calle Sierpes 48, Sevilla"] },
          ].map(({ i: Icon, l, v }) => (
            <div key={l} className="flex gap-4">
              <Icon size={20} className="text-gold mt-1 shrink-0" strokeWidth={1.3} />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold/80">{l}</div>
                {v.map((line) => (
                  <div key={line} className="text-foreground/85 mt-1 text-sm">
                    {line}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <OfficeMap />
    </Layout>
  );
}
